import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';

// Consent / Compliance viewer — relocated from the former standalone Admin
// "Audit" tab into Communications. Read-only over profiles' consent fields
// (/admin/consent-audit) with search, status filter and CSV export. Consent
// ENFORCEMENT lives entirely in the SMS backend + sms_opt_outs table and is
// unaffected by this screen.
export default function ConsentCompliancePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getConsentAudit();
        setRows(res.data.rows || []);
      } catch (err) {
        toast.error('Could not load consent audit');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : '—');

  const filtered = rows.filter((r) => {
    if (statusFilter !== 'all' && r.account_status !== statusFilter) return false;
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return [r.name, r.email, r.phone].some((v) => (v || '').toLowerCase().includes(q));
  });

  const counts = rows.reduce((acc, r) => {
    acc.total += 1;
    if (r.sms_consent) acc.with_sms += 1;
    if (r.sms_opt_in) acc.pref_on += 1;
    if (r.sms_opt_in && !r.sms_consent) acc.pref_only += 1;
    return acc;
  }, { total: 0, with_sms: 0, pref_on: 0, pref_only: 0 });

  const exportCsv = () => {
    const cols = ['name','email','phone','account_status','sms_consent','sms_opt_in','sms_consent_timestamp','terms_accepted_timestamp','consent_version','registration_ip_address','created_at'];
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [cols.join(','), ...filtered.map((r) => cols.map((c) => escape(r[c])).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consent-audit-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="w-6 h-6 border-2 border-[#015e3f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statusPillCls = (s) => {
    if (s === 'pending_approval') return 'bg-[#c2a251]/15 text-[#8a6d2c]';
    if (s === 'denied') return 'bg-red-50 text-red-700';
    if (s === 'invited') return 'bg-[#015e3f]/10 text-[#015e3f]';
    return 'bg-[#015e3f]/10 text-[#015e3f]'; // approved
  };

  return (
    <div className="space-y-4" data-testid="consent-audit-tab">
      <div className="bg-white border border-[#e3dcd1] rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-serif text-xl text-[#013523] tracking-tight">Consent / Compliance</h2>
            <p className="text-sm text-[#6a766e] mt-1">
              {counts.total} accounts · <strong className="text-[#013523]">{counts.with_sms} verified SMS consent</strong> · {counts.pref_on} app preference on · used for Twilio/TCR review.
            </p>
            {counts.pref_only > 0 && (
              <p data-testid="consent-gap-note" className="text-xs text-[#8a6d2c] mt-1.5 bg-[#c2a251]/12 border border-[#c2a251]/30 rounded-lg px-2.5 py-1.5 inline-block">
                <strong>{counts.pref_only}</strong> member{counts.pref_only === 1 ? '' : 's'} have the SMS notification preference on but <strong>no verified consent record</strong> — bulk SMS does <strong>not</strong> message them. "Verified consent" (affirmative, timestamped) is the only basis for sending.
              </p>
            )}
          </div>
          <button
            data-testid="audit-export-csv"
            onClick={exportCsv}
            className="bg-[#015e3f] hover:bg-[#014d34] text-white rounded-full px-4 py-2 text-sm font-medium"
          >
            Export CSV
          </button>
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <input
            data-testid="audit-search"
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name, email, or phone…"
            className="flex-1 min-w-[200px] bg-white border border-[#e3dcd1] rounded-full px-4 py-2 text-sm text-[#013523] placeholder-[#9aa39d] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/30"
          />
          <select
            data-testid="audit-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border border-[#e3dcd1] rounded-full px-3 py-2 text-sm text-[#013523]"
          >
            <option value="all">All statuses</option>
            <option value="approved">Approved</option>
            <option value="pending_approval">Pending</option>
            <option value="denied">Denied</option>
            <option value="invited">Invited</option>
          </select>
        </div>
      </div>

      {/* Mobile-friendly card list (sm and below) */}
      <ul className="space-y-2 sm:hidden">
        {filtered.map((r) => (
          <li
            key={r.id}
            data-testid={`audit-card-${r.id}`}
            className="bg-white border border-[#e3dcd1] rounded-2xl p-4 space-y-2"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-[#013523]">{r.name}</div>
                <div className="text-xs text-[#6a766e]">{r.email}</div>
                {r.phone && <div className="text-xs text-[#6a766e]">{r.phone}</div>}
              </div>
              <span className={`shrink-0 text-[10px] uppercase tracking-[0.18em] rounded-full px-2.5 py-1 ${statusPillCls(r.account_status)}`}>
                {r.account_status}
              </span>
            </div>
            <dl className="text-[11px] text-[#6a766e] grid grid-cols-2 gap-x-4 gap-y-0.5 pt-1 border-t border-[#f0ece3]">
              <dt className="opacity-70">Verified consent</dt>
              <dd>{r.sms_consent ? '✓ Yes' : '— No'}</dd>
              <dt className="opacity-70">App preference</dt>
              <dd className={r.sms_opt_in && !r.sms_consent ? 'text-[#8a6d2c]' : ''}>{r.sms_opt_in ? (r.sms_consent ? 'on' : 'on ⚠') : 'off'}</dd>
              <dt className="opacity-70">Consent time</dt>
              <dd className="font-mono">{fmt(r.sms_consent_timestamp)}</dd>
              <dt className="opacity-70">Terms accepted</dt>
              <dd className="font-mono">{fmt(r.terms_accepted_timestamp)}</dd>
              <dt className="opacity-70">Consent v</dt>
              <dd className="font-mono">{r.consent_version || '—'}</dd>
              <dt className="opacity-70">Reg IP</dt>
              <dd className="font-mono">{r.registration_ip_address || '—'}</dd>
            </dl>
          </li>
        ))}
        {filtered.length === 0 && (
          <li className="bg-white border border-[#e3dcd1] rounded-2xl p-6 text-center text-sm text-[#6a766e]">
            No records match.
          </li>
        )}
      </ul>

      {/* Desktop table (sm+) */}
      <div className="hidden sm:block bg-white border border-[#e3dcd1] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f5f0e6] text-[10px] uppercase tracking-[0.15em] text-[#6a766e]">
              <tr>
                <th className="text-left px-4 py-3 font-medium">User</th>
                <th className="text-left px-4 py-3 font-medium">Phone</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Verified Consent</th>
                <th className="text-left px-4 py-3 font-medium">App Pref</th>
                <th className="text-left px-4 py-3 font-medium">Consent Time</th>
                <th className="text-left px-4 py-3 font-medium">Terms</th>
                <th className="text-left px-4 py-3 font-medium">Version</th>
                <th className="text-left px-4 py-3 font-medium">Reg IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f0ece3]">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`} className="hover:bg-[#fbfaf8]">
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium text-[#013523]">{r.name}</div>
                    <div className="text-xs text-[#6a766e]">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 align-top text-[#2a332c]">{r.phone || '—'}</td>
                  <td className="px-4 py-3 align-top">
                    <span className={`text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5 ${statusPillCls(r.account_status)}`}>
                      {r.account_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">{r.sms_consent ? '✓ Yes' : '— No'}</td>
                  <td className="px-4 py-3 align-top">
                    {r.sms_opt_in ? (
                      <span className={r.sms_consent ? 'text-[#015e3f]' : 'text-[#8a6d2c]'} title={r.sms_consent ? '' : 'Preference on but no verified consent'}>
                        on{!r.sms_consent && ' ⚠'}
                      </span>
                    ) : 'off'}
                  </td>
                  <td className="px-4 py-3 align-top text-xs font-mono text-[#2a332c]">{fmt(r.sms_consent_timestamp)}</td>
                  <td className="px-4 py-3 align-top text-xs font-mono text-[#2a332c]">{fmt(r.terms_accepted_timestamp)}</td>
                  <td className="px-4 py-3 align-top text-xs font-mono">{r.consent_version || '—'}</td>
                  <td className="px-4 py-3 align-top text-xs font-mono">{r.registration_ip_address || '—'}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-sm text-[#6a766e]">
                    No records match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
